import { BizAoiBizTagInput } from '../../aoi-platform/graphql/mutations/upsertAoiAttribute';
import { AoiBizTagDictItem } from '../graphql/queries/listAoiBizTagDictionary';
import { BizTagDraft } from '../types';

/**
 * AOI 业务标签的草稿 ↔ 入参转换与字典派生 (纯函数; 抽屉的 dirty 比对直接拿 toBizTagInputs 的结果做
 * JSON.stringify 对比, 所以字段顺序必须稳定)。
 */

/** detail 返回的标签 → 抽屉草稿 (uid / kind / coverZipcodes / version 都是服务端算的, 不回传)。 */
export function toBizTagDrafts(
    tags: { tag: string; remark: string | null; paramsJson?: string | null }[] | undefined | null,
): BizTagDraft[] {
    return (tags ?? []).map((t) => ({
        tag: t.tag,
        remark: t.remark ?? null,
        paramsJson: t.paramsJson ?? null,
    }));
}

/** 抽屉草稿 → 提交入参: code 大写 trim, 空备注归 null, 丢掉 code 为空的行, 同 code 只保留第一条。 */
export function toBizTagInputs(tags: BizTagDraft[]): BizAoiBizTagInput[] {
    const seen = new Set<string>();
    const out: BizAoiBizTagInput[] = [];
    for (const t of tags) {
        const code = t.tag.trim().toUpperCase();
        if (code === '' || seen.has(code)) continue;
        seen.add(code);
        out.push({
            tag: code,
            paramsJson: t.paramsJson?.trim() || null,
            remark: t.remark?.trim() || null,
        });
    }
    return out;
}

/** code → 中文名 (字典没有的旧 code 回落为 code 本身)。 */
export function bizTagNameMap(dictionary: AoiBizTagDictItem[]): Record<string, string> {
    const map: Record<string, string> = {};
    dictionary.forEach((d) => {
        map[d.code] = d.zhName;
    });
    return map;
}

export function bizTagLabel(code: string, names: Record<string, string>): string {
    return names[code] ?? code;
}

/** 标签能否挂到该层级的面上 (DB 口径 level; 层级未知时不拦, 交给服务端)。 */
export function bizTagAllowedForLevel(item: AoiBizTagDictItem, dbLevel: number | null | undefined): boolean {
    if (dbLevel == null) return true;
    return item.allowedDbLevels.length === 0 || item.allowedDbLevels.includes(dbLevel);
}

/** allowedDbLevels → "仅 2 级" 这类提示; 两级都允许返回 null。 */
export function bizTagLevelHint(item: AoiBizTagDictItem): string | null {
    const levels = item.allowedDbLevels;
    if (levels.length === 0 || (levels.includes(0) && levels.includes(1))) return null;
    if (levels.includes(1)) return '仅 2 级';
    if (levels.includes(0)) return '仅 1 级';
    return null;
}

/** 标签种类 → 分组标题。 */
export const BIZ_TAG_KIND_LABELS: Record<string, string> = {
    FACT: '事实标签',
    POLICY: '策略标签',
};

export function bizTagKindLabel(kind: string): string {
    return BIZ_TAG_KIND_LABELS[kind] ?? kind;
}
